import React, { useState } from 'react';
import { Encounter } from '../../types/encounter';
import { CheckCircle, PenTool, User, Calendar, Shield } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { getAuthToken } from '../../utils/authUtils';

interface SignEncounterSectionProps {
  encounter: Encounter | null;
  onUpdate: () => void;
}

const SignEncounterSection: React.FC<SignEncounterSectionProps> = ({ encounter, onUpdate }) => {
  const [isSigning, setIsSigning] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!encounter) return null;

  const handleSign = async () => {
    if (!signerName.trim()) {
      setError('Please enter your name to sign');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_ENDPOINTS.ENCOUNTERS.BASE}/${encounter.id}/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          signer_name: signerName,
          signature_data: 'cursive-text', // Placeholder for now, could be image data
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to sign encounter');
      }

      onUpdate();
      setIsSigning(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (encounter.status === 'completed') {
    return (
      <div className="p-8">
        <h2 className="text-2xl font-semibold mb-6 flex items-center text-green-800">
          <CheckCircle className="w-6 h-6 mr-2" />
          Encounter Completed
        </h2>

        <div className="bg-white rounded-xl shadow-sm border border-green-100 overflow-hidden">
          <div className="bg-green-50 px-6 py-4 border-b border-green-100 flex justify-between items-center">
            <span className="font-medium text-green-800">Signed & Verified</span>
            <Shield className="w-5 h-5 text-green-600" />
          </div>

          <div className="p-8">
            <div className="flex flex-col md:flex-row gap-8 items-center justify-center">
              {/* Signature Preview */}
              <div className="flex-1 w-full text-center">
                <div className="text-sm text-gray-500 uppercase tracking-wider mb-2">Digital Signature</div>
                <div className="font-cursive text-4xl text-blue-900 py-6 border-b-2 border-gray-100 inline-block min-w-[200px]" style={{ fontFamily: '"Dancing Script", cursive' }}>
                  {encounter.signature_data?.signer_name || 'Doctor'}
                </div>
              </div>

              {/* Metadata */}
              <div className="flex-1 w-full space-y-4 bg-gray-50 p-6 rounded-lg border border-gray-100">
                <div className="flex items-center">
                  <User className="w-4 h-4 text-gray-400 mr-3" />
                  <div>
                    <div className="text-xs text-gray-500">Signed By</div>
                    <div className="font-medium text-gray-900">{encounter.signature_data?.signer_name}</div>
                  </div>
                </div>

                <div className="flex items-center">
                  <Calendar className="w-4 h-4 text-gray-400 mr-3" />
                  <div>
                    <div className="text-xs text-gray-500">Date Signed</div>
                    <div className="font-medium text-gray-900">
                      {encounter.signature_data?.signed_at ? new Date(encounter.signature_data.signed_at).toLocaleString() : '-'}
                    </div>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-4 h-4 flex items-center justify-center mr-3">
                    <span className="text-[10px] font-bold text-gray-400 border border-gray-400 rounded px-0.5">IP</span>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">IP Address</div>
                    <div className="font-mono text-sm text-gray-600">{encounter.signature_data?.ip_address || 'Not recorded'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h2 className="text-2xl font-semibold mb-4">Sign & Complete Encounter</h2>

      {!isSigning ? (
        <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200 text-center">
          <div className="mb-6 text-gray-600 max-w-lg mx-auto">
            By signing this encounter, you confirm that all documentation is accurate and complete.
            Once signed, the encounter will be locked and no further edits can be made.
          </div>
          <button
            onClick={() => setIsSigning(true)}
            className="px-8 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center mx-auto"
          >
            <PenTool className="w-4 h-4 mr-2" />
            Sign Encounter
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 max-w-2xl mx-auto overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">Finalize Documentation</h3>
          </div>

          <div className="p-6 space-y-6">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm border border-red-100">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name (Signature)
              </label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Dr. Jane Doe"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                autoFocus
              />
            </div>

            {signerName && (
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-center">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Signature Preview</div>
                <div className="font-cursive text-3xl text-blue-900" style={{ fontFamily: '"Dancing Script", cursive' }}>
                  {signerName}
                </div>
              </div>
            )}

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => setIsSigning(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSign}
                disabled={isLoading || !signerName.trim()}
                className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isLoading ? 'Signing...' : 'Sign & Complete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignEncounterSection;

